import React, { useContext, useState } from 'react';
import { useApiRequest } from '../../api/useApiRequest';
import { Alert } from '../../shared/Alert';
import { PrimaryProgressButton, SecondaryButton } from '../../shared/Button';
import { Modal, ModalActionButtons, ModalErrorBox, ModalHeader } from '../../shared/Modal';
import { ProjectContext } from '../projects/project-section/ProjectContext';

type Props = {
  projectId: number;
  quoteId: number;
  isOpen: boolean;
  closeModal: () => void;
};

export const UploadQuoteToNetSuiteModal = ({ projectId, quoteId, isOpen, closeModal }: Props) => {
  const uploadToNetSuiteRequest = useApiRequest<undefined, UploadQuoteToNetSuiteCommand>({
    httpMethod: 'post',
    endpointUrl: `projects/${projectId}/quotes/${quoteId}/upload-to-net-suite`,
  });

  const { inProgress, error } = uploadToNetSuiteRequest;

  const [success, setSuccess] = useState(false);

  const sendRequest = () => {
    uploadToNetSuiteRequest.sendRequest({
      requestBody: {
        quoteId,
      },
      onSuccess: () => setSuccess(true),
    });
  };

  const { reloadProject } = useContext(ProjectContext);

  const onClose = () => {
    if (success) {
      reloadProject();
    }

    closeModal();
  };

  if (success) {
    return (
      <Modal isOpen={isOpen}>
        <Alert type="success" title="Success" body="The Quote was successfully uploaded to NetSuite." />
        <ModalActionButtons $align="right">
          <SecondaryButton onClick={onClose}>Close</SecondaryButton>
        </ModalActionButtons>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen}>
      <ModalHeader>Upload Quote to NetSuite</ModalHeader>
      <p>Are you sure you wish to upload this Quote to NetSuite?</p>
      <p>
        Once uploaded you will not be able to edit this Quote. To make changes to this Quote you will need to create a
        new revision.
      </p>
      <p>This action cannot be undone.</p>
      {error && <ModalErrorBox error={error} />}
      <ModalActionButtons $align="right">
        <SecondaryButton onClick={onClose} disabled={inProgress}>
          Cancel
        </SecondaryButton>
        <PrimaryProgressButton onClick={sendRequest} inProgress={inProgress}>
          Confirm
        </PrimaryProgressButton>
      </ModalActionButtons>
    </Modal>
  );
};

type UploadQuoteToNetSuiteCommand = {
  quoteId: number;
};
